// Saved addresses tab list + detail.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { SavedAddress } from '../mockData.js';
import { WriteButton } from '../../_w2a_lower/liveWrite.js';

export interface SavedAddressesCardProps {
  addresses: SavedAddress[];
  activeId: string;
  onSelect: (id: string) => void;
  onEdit: () => void;
  onAdd: () => void;
  /** W31: true on a real session; edit / add stay visible but inert. */
  liveWritesPaused?: boolean;
}

export function SavedAddressesCard({
  addresses,
  activeId,
  onSelect,
  onEdit,
  onAdd,
  liveWritesPaused = false,
}: SavedAddressesCardProps): ReactNode {
  const active = addresses.find(a => a.id === activeId) ?? addresses[0];
  return (
    <section className="card ca-card">
      <div className="ca-card-head">
        <div>
          <Icon id="pin" />
          <h2>Saved addresses</h2>
        </div>
        <span id="ca-address-count">
          {addresses.length} {addresses.length === 1 ? 'address' : 'addresses'}
        </span>
      </div>
      <div className="ca-body ca-address-body">
        {addresses.length === 0 ? (
          <p
            role="status"
            data-testid="customer-addresses-empty"
            className="muted"
            style={{ margin: '0 0 12px' }}
          >
            No saved addresses on file
          </p>
        ) : (
          <div
            className="ca-address-tabs"
            id="ca-address-tabs"
            role="group"
            aria-label="Saved addresses"
          >
            {addresses.map(addr => (
              <button
                key={addr.id}
                type="button"
                aria-pressed={active?.id === addr.id}
                className={`ca-address-tab${active?.id === addr.id ? ' current' : ''}`}
                onClick={() => onSelect(addr.id)}
              >
                <strong>{addr.label}</strong>
                {addr.current ? <small>Current destination</small> : null}
              </button>
            ))}
          </div>
        )}
        <div className="ca-address-detail" id="ca-address-detail">
          {active ? (
            <>
              <p>
                <strong>{active.recipient}</strong>
              </p>
              <p>{active.line1}</p>
              <p>
                {active.city}, {active.state} {active.zip}
              </p>
            </>
          ) : null}
        </div>
        <div className="ca-address-bottom">
          <div>
            <WriteButton
              paused={liveWritesPaused}
              className="ca-secondary"
              id="ca-edit-address"
              onClick={onEdit}
              actionLabel="Edit address"
            >
              <Icon id="pin" />
              Edit address
            </WriteButton>
            <WriteButton
              paused={liveWritesPaused}
              className="ca-link"
              id="ca-add-address"
              onClick={onAdd}
              actionLabel="Add address"
            >
              + Add address
            </WriteButton>
          </div>
          <p>Saving an address keeps existing orders and subscriptions intact.</p>
        </div>
      </div>
    </section>
  );
}
